/**
 * Version Check Utility
 *
 * This utility checks if the application version has changed and clears all
 * client-side storage (localStorage, sessionStorage, IndexedDB) when a new
 * version is detected. This prevents stale data from causing issues when the
 * application is updated.
 */

// Import package.json to get the current version
// @ts-ignore - package.json import works with Vite but may show TypeScript errors
import packageJson from '../../package.json';

const VERSION_KEY = 'app_version';
const CURRENT_VERSION = (packageJson as { version: string }).version;

/**
 * Clears all client-side storage
 */
async function clearAllStorage(): Promise<void> {
    console.log('🧹 Clearing all client-side storage due to version change...');

    try {
        // Clear localStorage
        localStorage.clear();
        console.log('✅ localStorage cleared');

        // Clear sessionStorage
        sessionStorage.clear();
        console.log('✅ sessionStorage cleared');

        // Clear IndexedDB databases
        if (window.indexedDB) {
            const databases = await window.indexedDB.databases();
            for (const db of databases) {
                if (db.name) {
                    window.indexedDB.deleteDatabase(db.name);
                    console.log(`✅ IndexedDB database "${db.name}" cleared`);
                }
            }
        }

        // Clear service worker caches if available
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            for (const cacheName of cacheNames) {
                await caches.delete(cacheName);
                console.log(`✅ Cache "${cacheName}" cleared`);
            }
        }

        console.log('✅ All client-side storage cleared successfully');
    } catch (error) {
        console.error('❌ Error clearing storage:', error);
    }
}

/**
 * Checks if the application version has changed and clears storage if needed
 * Returns true if storage was cleared (indicating a version change)
 */
export async function checkAndHandleVersionChange(): Promise<boolean> {
    try {
        const storedVersion = localStorage.getItem(VERSION_KEY);

        if (!storedVersion) {
            // First time running or localStorage was cleared
            console.log(`📦 Initializing app version: ${CURRENT_VERSION}`);
            localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
            return false;
        }

        if (storedVersion !== CURRENT_VERSION) {
            // Version has changed - clear all storage
            console.log(`🔄 Version changed from ${storedVersion} to ${CURRENT_VERSION}`);

            await clearAllStorage();

            // Set new version AFTER clearing (since clearAllStorage clears localStorage)
            localStorage.setItem(VERSION_KEY, CURRENT_VERSION);

            // Optionally show a notification to the user
            console.log(`✨ Application updated to version ${CURRENT_VERSION}`);

            return true;
        }

        // No version change
        return false;
    } catch (error) {
        console.error('❌ Error during version check:', error);
        return false;
    }
}

/**
 * Gets the current application version
 */
export function getCurrentVersion(): string {
    return CURRENT_VERSION;
}

/**
 * Gets the stored application version
 */
export function getStoredVersion(): string | null {
    return localStorage.getItem(VERSION_KEY);
}
